import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import JmlLink from "@/components/JmlLink";
import MarketingShell from "@/components/MarketingShell";
import { SOCIAL, DAN_SOCIAL } from "@/lib/links";

export default function Music() {
  return (
    <MarketingShell active="/music">
      <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Music
        </p>
        <h1
          className="leading-[0.95]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(40px, 5.6vw, 84px)",
            textWrap: "balance",
            maxWidth: "20ch",
          }}
        >
          Recordings, improvisations, &amp; the <em style={{ color: "#F2D589" }}>occasional</em> live take.
        </h1>
        <p className="mt-5 max-w-[44rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          Press play. Most of what&apos;s here is solo piano, some studio, some captured in the moment. New material drops
          regularly wherever you listen.
        </p>
      </motion.section>

      <p className="mt-12 text-[10px] uppercase tracking-[0.28em]" style={{ color: "#F2D589" }} data-testid="music-release-eyebrow">
        Out now &middot; {DAN_SOCIAL.spotifyAlbumTitle}
      </p>
      <section className="mt-4 grid lg:grid-cols-2 gap-5">
        <div
          className="rounded-2xl overflow-hidden border"
          style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.6)", minHeight: "450px" }}
          data-testid="music-spotify"
        >
          {/* The album, not the artist: an artist embed shows "top tracks",
              which stays empty on a new profile. URL is exactly what Spotify's
              oembed returns — improvising on the params makes it throw. */}
          <iframe
            title={`Spotify Embed: ${DAN_SOCIAL.spotifyAlbumTitle}`}
            width="100%"
            height="450"
            frameBorder="0"
            loading="lazy"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            src="https://open.spotify.com/embed/album/2iK9wQ8brZN1fudBsywvsk?utm_source=oembed"
            style={{ display: "block" }}
          />
        </div>
        <div
          className="rounded-2xl overflow-hidden border"
          style={{ borderColor: "rgba(255,255,255,0.12)" }}
          data-testid="music-youtube"
        >
          <iframe
            title="Dan Berry on YouTube"
            width="100%"
            height="450"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            src="https://www.youtube.com/embed/NNvMWb0mp78?rel=0&modestbranding=1"
            style={{ display: "block" }}
          />
        </div>
      </section>

      <section className="mt-12 flex flex-wrap gap-3 text-sm">
        <a
          href={DAN_SOCIAL.spotify}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full px-5 h-11 border border-white/25 hover:border-[#F2D589] hover:text-[#F2D589] transition-colors uppercase tracking-[0.2em]"
          data-testid="music-spotify-link"
        >
          Spotify &rsaquo;
        </a>
        <a
          href={DAN_SOCIAL.appleMusic}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full px-5 h-11 border border-white/25 hover:border-[#F2D589] hover:text-[#F2D589] transition-colors uppercase tracking-[0.2em]"
          data-testid="music-apple-link"
        >
          Apple Music &rsaquo;
        </a>
        <a
          href={SOCIAL.youtube}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full px-5 h-11 border border-white/25 hover:border-[#F2D589] hover:text-[#F2D589] transition-colors uppercase tracking-[0.2em]"
          data-testid="music-youtube-link"
        >
          YouTube &rsaquo;
        </a>
        <Link
          to="/commissions"
          className="inline-flex items-center rounded-full px-5 h-11 border border-white/25 hover:border-[#F2D589] hover:text-[#F2D589] transition-colors uppercase tracking-[0.2em]"
          data-testid="music-commission-link"
        >
          Commission something new &rsaquo;
        </Link>
      </section>

      {/* The Alephonics podcast */}
      <section
        className="mt-20 rounded-2xl border p-6 sm:p-10 backdrop-blur-md"
        style={{ borderColor: "rgba(255,255,255,0.14)", backgroundColor: "rgba(8,4,30,0.5)" }}
        data-testid="music-alephonics"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Every week
        </p>
        <h2
          className="text-3xl sm:text-4xl leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, textWrap: "balance", maxWidth: "28ch" }}
        >
          The Alephonics podcast.
        </h2>
        <p className="mt-4 max-w-[48rem] text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.78)" }}>
          A new piece every week, built from the word at the center of the week.
          Hebrew letters mapped into music with the{" "}
          <strong style={{ color: "#F2D589", fontWeight: 400 }}>Alephonic Mapping Method&trade;</strong>, then played
          live — so the same word comes out the same shape every time, and never
          quite the same performance twice.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/podcast"
            className="inline-flex items-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.2em] transition-colors hover:bg-[#F2D589]/10"
            style={{ color: "#F2D589", border: "1px solid rgba(242,213,137,0.5)" }}
            data-testid="music-podcast-link"
          >
            Hear the episodes &rarr;
          </Link>
          <JmlLink
            className="inline-flex items-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.2em] border border-white/25 hover:bg-white/10 transition-colors"
            testId="music-method-link"
          >
            How the method works
          </JmlLink>
        </div>
      </section>

      {/* Support the music */}
      <section
        className="mt-12 rounded-2xl border p-6 sm:p-10 backdrop-blur-md"
        style={{ borderColor: "rgba(242,213,137,0.35)", backgroundColor: "rgba(12,8,38,0.6)" }}
        data-testid="music-support"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Support the music
        </p>
        <h2 className="text-2xl sm:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          There&apos;s no label behind this.
        </h2>
        <p className="mt-3 max-w-[46rem] text-sm sm:text-base" style={{ color: "rgba(245,239,227,0.75)", lineHeight: 1.7 }}>
          $5, $18 or $36 a month keeps the weekly podcast coming, puts your name
          on the Patrons wall, and credits you on any album released while
          you&apos;re a patron.
        </p>
        <div className="mt-7">
          <Link
            to="/patrons"
            className="inline-flex items-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.2em]"
            style={{ backgroundColor: "#F2D589", color: "#080419", fontWeight: 600 }}
            data-testid="music-patrons-link"
          >
            Support the music &rarr;
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
